import { ReactNode, useEffect, useState } from "react";
import { useJwt } from "react-jwt";
import { Location, useLocation } from "react-router-dom";

import { AuthContext, AuthContextType } from "@/contexts/authContext";
import { authService } from "@/services/auth";
import { toUser } from "@/types/user";
import { isPublicRoute } from "@/utils/publicRoutes";

// high level component, wraps most of app
// passes auth params to children components
// upon state changes, re-assesses expiration of access token
const Authentication = ({
  children,
}: {
  children: (authState: AuthContextType, location: Location) => ReactNode; // TODO: remove authState render prop pattern in favor of new context
}) => {
  // state
  // TODO SECURITY: Tokens stored in localStorage are vulnerable to XSS attacks.
  // The secure solution is to use HttpOnly cookies, which requires backend API changes
  // to set cookies instead of returning tokens in JSON responses.
  // This should be addressed in a dedicated security ticket.
  const [accessToken, setAccessToken] = useState(
    localStorage.getItem("accessToken")
  );

  const { isExpired } = useJwt(accessToken || "");
  const location = useLocation();

  // effects
  // NOTE: Token refresh is now handled entirely by the Axios interceptor in utils/axios.js
  // This prevents duplicate refresh attempts and ensures a single point of control
  // The interceptor will automatically:
  // 1. Detect 401 errors
  // 2. Queue concurrent requests during refresh
  // 3. Redirect to login if refresh fails
  // 4. Update localStorage with new tokens

  // Listen for token refresh events from Axios interceptor
  // NOTE: Using browser custom events instead of React state management because:
  // 1. Token refresh happens in axios.js (plain JS), outside React's component tree
  // 2. This provides clean cross-boundary communication without coupling axios to React
  // 3. Multiple components can listen if needed without prop drilling
  useEffect(() => {
    const handleTokenRefresh = (event: CustomEvent) => {
      const newToken = event.detail.accessToken;
      if (newToken) {
        setAccessToken(newToken);
      }
    };

    window.addEventListener(
      "tokenRefreshed",
      handleTokenRefresh as EventListener
    );

    return () => {
      window.removeEventListener(
        "tokenRefreshed",
        handleTokenRefresh as EventListener
      );
    };
  }, []);

  useEffect(() => {
    if (location?.pathname?.includes("/link/dynamic")) {
      return;
    }

    if (isPublicRoute(location.pathname)) {
      return;
    }

    if (accessToken && !isExpired) {
      authService.fetchUser();
    }
  }, [accessToken, isExpired]); // Refetches user config when a new access token is set

  // clean slate of props we return to all components
  let authState: AuthContextType = {
    authenticated: false,
  };

  // For public routes, always treat as unauthenticated regardless of token state
  if (isPublicRoute(location.pathname)) {
    authState = Object.assign(authState, {
      authenticated: false,
    });
  } else if (accessToken && !isExpired) {
    // we're ready to rock!
    authState = Object.assign(authState, {
      authenticated: true,
      accessToken: localStorage.getItem("accessToken"),
      refreshToken: localStorage.getItem("refreshToken"),
      currentUser: toUser(
        JSON.parse(localStorage.getItem("currentUser") || "{}")
      ),
    });
  } else {
    // Not authenticated or token is expired
    // The Axios interceptor will handle token refresh attempts and redirect if needed
    // Treat as unauthenticated and let protected routes handle the login redirect
    authState = Object.assign(authState, {
      authenticated: false,
    });
  }

  // NOTE: This component uses BOTH Context API and render props to provide auth state.
  // This is pre-existing technical debt (see TODO on line 16).
  // - Context API (modern): Components can use useContext(AuthContext) or custom hooks
  // - Render Props (legacy): App.tsx receives authState as a function parameter
  // TODO: Migrate all consumers to Context API and remove render props pattern
  return (
    <AuthContext.Provider value={authState}>
      {children(authState, location)}
    </AuthContext.Provider>
  );
};

export default Authentication;
